import { createEnum } from '../helpers'
import { type BackMarginsNodeKey, type PnlNodeKey } from './pnl'

/**
 * Which year one side of a variance comparison reads, relative to its CRT period. `CY` and `NY` are the
 * summary's forecast years and read a saved version; `LY` is the year *before* the period, read as
 * actuals only (the yearly BPC bucket, live, no version). Deliberately a separate list from the global
 * `YEAR_FLAG_VALUES`, which every forecast source branches on as a binary — a third value there would
 * silently fall into the current-year branch everywhere.
 */
export const VARIANCE_SIDE_YEARS = ['LY', 'CY', 'NY'] as const
export const VARIANCE_SIDE_YEAR = createEnum(VARIANCE_SIDE_YEARS)
export type VarianceSideYear = (typeof VARIANCE_SIDE_YEARS)[number]

/**
 * The measures reported when two CRT versions are compared.
 *
 * They are **not** a single additive decomposition. Each one is its own KPI, read in its own unit — the NTO
 * variance in dollars, the price variance in dollars per unit, the volume variance in units, the mix
 * variances in dollars — so `total = price + volume + mixTotal` is deliberately *not* claimed anywhere: it
 * would be adding dollars to units. Every measure is `B − A` at the row grain, and a card is the sum of its
 * own breakdown's variance column and nothing else.
 *
 * The one roll-up that does hold is within mix: `mixTotal` is exactly the sum of its seven components,
 * which is what {@link MIX_VARIANCE_TYPES}'s nested attribution is built to guarantee.
 *
 * Card order and the variance-type bar both read these lists, so the order here is display order.
 */

/**
 * The headline figure: NTO on the compare side less NTO on the base side. It sits on the summary card
 * alongside each option's own NTO, volume and rate, and is deliberately not selectable as a lens — a table
 * keyed on it would just restate the row's NTO difference.
 */
export const TOTAL_VARIANCE_TYPE = 'total'

/** The lead cards. Three independent readings of the same comparison, each in its own unit. */
export const TOP_LEVEL_VARIANCE_TYPES = ['price', 'volume', 'mixTotal'] as const

/**
 * The mix attributions, in display order — four cards on the first row, three on the second.
 *
 * The order is display order only. These are **not** nested levels of one decomposition: each entry pools
 * the rate over a different dimension and re-values the *same* volume shift against it, so all seven are
 * alternative readings of one comparison rather than slices of it, and two of them can attribute the same
 * shift. `mixTotal` is their sum, which makes it a total of overlapping readings and not a figure the seven
 * divide up. Reordering this list therefore changes the layout and nothing else.
 */
export const MIX_VARIANCE_TYPES = [
  'mixProvince',
  'mixSegment',
  'mixBrand',
  'mixPackContentSize',
  'mixProductType',
  'mixSubChannel',
  'mixOther',
] as const

export const VARIANCE_TYPES = [
  TOTAL_VARIANCE_TYPE,
  ...TOP_LEVEL_VARIANCE_TYPES,
  ...MIX_VARIANCE_TYPES,
] as const

/** What the variance-type bar offers, and what the table's value column can report. */
export const SELECTABLE_VARIANCE_TYPES = [
  ...TOP_LEVEL_VARIANCE_TYPES,
  ...MIX_VARIANCE_TYPES,
] as const

export type TopLevelVarianceType = (typeof TOP_LEVEL_VARIANCE_TYPES)[number]
export type MixVarianceType = (typeof MIX_VARIANCE_TYPES)[number]
export type VarianceType = (typeof VARIANCE_TYPES)[number]
export type SelectableVarianceType = (typeof SELECTABLE_VARIANCE_TYPES)[number]

/**
 * What a variance figure is denominated in. The measures no longer share a unit, so nothing may format one
 * without consulting this: rendering a unit count or a per-unit rate as a plain dollar amount is exactly
 * the misreading the decomposition was rewritten to remove.
 */
export const VARIANCE_UNITS = {
  /** A dollar amount. */
  CURRENCY: 'currency',
  /** Dollars per unit. */
  RATE: 'rate',
  /** A unit count — no dollars involved. */
  UNITS: 'units',
} as const
export type VarianceUnit = (typeof VARIANCE_UNITS)[keyof typeof VARIANCE_UNITS]

export const VARIANCE_TYPE_UNITS = {
  total: VARIANCE_UNITS.CURRENCY,
  // A rate variance is the shift in realised dollars per unit, summed over the breakdown's rows. It is not
  // a dollar amount and must never be shown as one.
  price: VARIANCE_UNITS.RATE,
  volume: VARIANCE_UNITS.UNITS,
  mixTotal: VARIANCE_UNITS.CURRENCY,
  mixProvince: VARIANCE_UNITS.CURRENCY,
  mixSegment: VARIANCE_UNITS.CURRENCY,
  mixBrand: VARIANCE_UNITS.CURRENCY,
  mixPackContentSize: VARIANCE_UNITS.CURRENCY,
  mixProductType: VARIANCE_UNITS.CURRENCY,
  mixSubChannel: VARIANCE_UNITS.CURRENCY,
  mixOther: VARIANCE_UNITS.CURRENCY,
} as const satisfies Record<VarianceType, VarianceUnit>

/** The lens selected when the URL carries none. */
export const DEFAULT_VARIANCE_TYPE: SelectableVarianceType = 'price'

/* -------------------------------------------------------------------------- */
/* Which measure is being compared                                             */
/* -------------------------------------------------------------------------- */

/**
 * The KPI a comparison is read against — the axis the whole page turns on, orthogonal to the product
 * category and to the lens above.
 *
 * Derived from {@link PnlNodeKey} rather than written out, so this vocabulary cannot drift from
 * the language the summary tree defines. `Extract`, not `Pick`: that alias is a string **union**, and
 * `Pick` over a union collapses to `{}` — the same trap {@link VarianceDrillingDimension} documents for
 * `Omit`.
 */
export type VarianceMeasure = Extract<PnlNodeKey, 'ar' | 'nto'>

/**
 * Both measures, in display order. The `satisfies` is what makes the derivation load-bearing rather than
 * decorative: were `ar` renamed in the summary tree, {@link VarianceMeasure} would quietly lose it and
 * this literal would stop compiling *here*, rather than somewhere downstream reading a key nothing
 * resolves.
 */
export const VARIANCE_MEASURES = ['nto', 'ar'] as const satisfies readonly VarianceMeasure[]

/**
 * The measure a request carries none for. NTO, so every URL written before AR existed keeps meaning what
 * it meant.
 */
export const DEFAULT_VARIANCE_MEASURE: VarianceMeasure = 'nto'

/**
 * Why a pair of sides is not comparable, as opposed to why one field of one side is wrong — which is
 * what the query schema's own per-field rules say. Emitted as a ZodIssue `params.type` so the frontend
 * can translate it, and named to match its i18n key under `varianceAnalysis`.
 *
 * The inverted range is split per side because the reader has to be told *which* range reads backwards;
 * the other two describe the pair, and name no side at all. `invertedRange` is the same fault on a
 * lone range — the current-totals query carries one side, so there is no A/B to disambiguate.
 */
export const VARIANCE_SIDES_ERRORS = [
  'invertedRangeA',
  'invertedRangeB',
  'invertedRange',
  'identicalSides',
  'rangeLengthMismatch',
] as const

export const VARIANCE_SIDES_ERROR_TYPES = createEnum(VARIANCE_SIDES_ERRORS)
export type VarianceSidesErrorType = (typeof VARIANCE_SIDES_ERRORS)[number]

/**
 * Why a filter a drill request carries is malformed, as opposed to unanswerable.
 *
 * The distinction is the status code. A filter group the target breakdown cannot take is a **422** with
 * the breakdowns that could have — the request is well formed and the reader can act on it. A group that
 * is not a group at all is a **400**: an agreement number names nothing without the master agreement it
 * belongs to, and a request carrying one alone has asked for something that does not exist. Silently
 * stripping it, which is what a bare optional field does, would answer the unfiltered question instead.
 *
 * Emitted as a ZodIssue `params.type` so the frontend can translate it, and named to match its i18n key
 * under `varianceAnalysis` — the same contract as {@link VARIANCE_SIDES_ERRORS}.
 */
export const VARIANCE_FILTER_ERRORS = ['agreementNumberWithoutMasterAgreement'] as const

export const VARIANCE_FILTER_ERROR_TYPES = createEnum(VARIANCE_FILTER_ERRORS)
export type VarianceFilterErrorType = (typeof VARIANCE_FILTER_ERRORS)[number]

/* -------------------------------------------------------------------------- */
/* The cost-type breakdown, and where a cell can be drilled                    */
/* -------------------------------------------------------------------------- */

/**
 * The cost types the back-margins breakdown reports, in display order.
 *
 * These are **back-margins KPI nodes**, not the investment types themselves: the three plain leaves a
 * type maps onto (`INVESTMENT_TYPE_TO_KPI_KEY`) plus the two parents whose children — the key-account
 * clause lines and the unmapped `otherP4p` / `otherMiInGto` — a customer investment can land in.
 *
 * `goP4p` is a **top-level row**, unlike the KPI tree where it is a child of `tradePayForPerformance`
 * (`computeTradePayForPerformance` rolls it up). That re-parenting is what makes these five *disjoint*
 * — every investment dollar lands in exactly one of them — which is what lets the page add a column up.
 * The price is that this `tradePayForPerformance` figure is deliberately **not** the summary's
 * `tradePayForPerformance` node: it excludes `goP4p`.
 */
export const VARIANCE_COST_TYPES = [
  'salesDeals',
  'commercialFund',
  'tradePayForPerformance',
  'goP4p',
  'marketingInvestmentInGTO',
] as const satisfies readonly BackMarginsNodeKey[]

export type VarianceCostType = (typeof VARIANCE_COST_TYPES)[number]

/**
 * The two KPI-summary lines reported beside the cost types, as context for the spend above them.
 *
 * They are **not** cost types and are deliberately absent from {@link VARIANCE_COST_TYPES}. That list is
 * also the drill-filter enum, the filter panel's option list, the `COST_TYPE_LINES` partition and the
 * input to {@link getValidVarianceDrillingDimensions} — so a key kept out of it cannot be expressed as a
 * `costType` filter, cannot reach a drill endpoint without a 422, and needs no line partition of its own.
 * That is what makes "these two rows cannot be drilled" structural rather than a rule to remember.
 *
 * Neither is additive with the cost types either. `startPrice` is NTO with the back margins and the
 * distributor margin added back (`computeStartPrice`), and `distributorMargins` already sits inside it —
 * so the column still adds up over the five cost types alone, and over nothing wider.
 */
export const VARIANCE_SUMMARY_KPIS = ['startPrice', 'distributorMargins'] as const

export type VarianceSummaryKpi = (typeof VARIANCE_SUMMARY_KPIS)[number]

/**
 * Every row `/by-cost-type` reports, per measure, in display order.
 *
 * One record keyed by the measure rather than a list each: the two are alternatives, never both at once,
 * and keying them by the axis that chooses between them is what lets a caller read the rows for the
 * measure it was handed without knowing which one that is.
 *
 * **NTO** — the start price it all comes out of, the five cost types, then the distributor margin.
 * Written out rather than spread from {@link VARIANCE_SUMMARY_KPIS}, because the order interleaves the
 * two lists: the summary lines bracket the partition rather than sitting beside it.
 *
 * **AR** — the cash ladder, mirroring the Cash Dashboard's own table so the two pages describe accounts
 * receivable the same way. It is not a partition and does not add up like one: the first five rows build
 * the three totals beneath them (`nto` → `gto` → `ar`, via `computeGto` / `computeAr`). `nto` is a row a
 * reader **switches the measure** on rather than drills into, which is the one link this ladder offers
 * besides `backMargins`.
 *
 * Every key is constrained to {@link PnlNodeKey}, so a row can only ever name a node the summary
 * tree actually has.
 */
export const VARIANCE_COST_TYPE_ROW_KEYS_BY_MEASURE = {
  nto: ['startPrice', ...VARIANCE_COST_TYPES, 'distributorMargins'],
  ar: [
    'volumes',
    'federalExcise',
    'directPtt',
    'totalDirectVat',
    'backMargins',
    'nto',
    'gto',
    'ar',
  ],
} as const satisfies Record<VarianceMeasure, readonly PnlNodeKey[]>

/**
 * The NTO ladder under its original name, derived rather than restated so the two cannot disagree.
 * {@link VARIANCE_COST_TYPE_ROW_KEYS_BY_MEASURE} is what a measure-aware caller reads.
 */
export const VARIANCE_COST_TYPE_ROW_KEYS = VARIANCE_COST_TYPE_ROW_KEYS_BY_MEASURE.nto

/** A row key of one measure's ladder, or of either when the measure is not known statically. */
export type VarianceCostTypeRowKey<M extends VarianceMeasure = VarianceMeasure> =
  (typeof VARIANCE_COST_TYPE_ROW_KEYS_BY_MEASURE)[M][number]

/**
 * What each `/by-cost-type` row is denominated in.
 *
 * Both ladders in one record, keyed by the row rather than by the measure, because the table formats a
 * row without knowing which ladder handed it over. Every key is spelled out and the `satisfies` is the
 * point: a row added to either ladder stops compiling until someone says what unit it is in, which is
 * the only thing standing between a unit count and the currency formatter.
 *
 * `volumes` is the one row that is not money. It heads the AR ladder because the cash figures beneath it
 * are the volume valued at a rate, and rendering it as dollars — worse, converting it at the selected FX
 * rate — is exactly the misreading {@link VARIANCE_UNITS} exists to prevent.
 */
export const VARIANCE_COST_TYPE_ROW_UNITS = {
  // The NTO ladder: a start price, the five cost types it is spent against, and the distributor margin.
  startPrice: VARIANCE_UNITS.CURRENCY,
  salesDeals: VARIANCE_UNITS.CURRENCY,
  commercialFund: VARIANCE_UNITS.CURRENCY,
  tradePayForPerformance: VARIANCE_UNITS.CURRENCY,
  goP4p: VARIANCE_UNITS.CURRENCY,
  marketingInvestmentInGTO: VARIANCE_UNITS.CURRENCY,
  distributorMargins: VARIANCE_UNITS.CURRENCY,
  // The AR ladder: a unit count, then the levies and credits that build the three totals.
  volumes: VARIANCE_UNITS.UNITS,
  federalExcise: VARIANCE_UNITS.CURRENCY,
  directPtt: VARIANCE_UNITS.CURRENCY,
  totalDirectVat: VARIANCE_UNITS.CURRENCY,
  backMargins: VARIANCE_UNITS.CURRENCY,
  nto: VARIANCE_UNITS.CURRENCY,
  gto: VARIANCE_UNITS.CURRENCY,
  ar: VARIANCE_UNITS.CURRENCY,
} as const satisfies Record<VarianceCostTypeRowKey, VarianceUnit>

/**
 * The unit one cost-type row reports in.
 *
 * Takes the loose key the drill table actually holds: `DrillRow.grouping` is a partial record of plain
 * strings, because the four drill pages share one flat dimension vocabulary and the row key arrives
 * through it. Currency for anything else, which is the same default the table applies to a breakdown
 * that names no units at all.
 */
export function getVarianceCostTypeRowUnit(kpiKey: string | null | undefined): VarianceUnit {
  return VARIANCE_COST_TYPE_ROW_UNITS[kpiKey as VarianceCostTypeRowKey] ?? VARIANCE_UNITS.CURRENCY
}

/**
 * Where a cost type's dollars came from.
 *
 * Three values rather than the nine `KpiSource` tags, because the question the page asks is which
 * *kind* of decision put the money there: something somebody imported, a program somebody entered by
 * hand, or a budget balance nobody has committed yet.
 *
 * - `directImport` — the RAP forecast for the forecast months, and BPC plus the actualised investment
 *   imports for the elapsed ones.
 * - `manualProgram` — the manual investment programs, forecast-only.
 * - `leftover` — the budget left-to-spend balance and the key-account contract leftovers, both
 *   forward-looking and neither read off a per-material row.
 */
export const VARIANCE_COST_SOURCES = ['directImport', 'manualProgram', 'leftover'] as const

export type VarianceCostSource = (typeof VARIANCE_COST_SOURCES)[number]

/**
 * Every way a variance comparison can be grouped — one per `/crt/variance-analysis/…` breakdown.
 *
 * `byCostType` is the head of the drill chain rather than a step in it: a reader arrives there from the
 * heat map and leaves it for one of the others. That is why it is the one dimension no cell can be
 * *drilled into*, and why {@link VarianceDrillingDimension} excludes it.
 */
export const VARIANCE_GROUPING_DIMENSIONS = [
  'byCostType',
  'byBudgetAllocation',
  'byAgreement',
  'byCustomer',
] as const

export const VARIANCE_GROUPING_DIMENSION_TYPES = createEnum(VARIANCE_GROUPING_DIMENSIONS)
export type VarianceGroupingDimension = (typeof VARIANCE_GROUPING_DIMENSIONS)[number]

/**
 * A dimension a cost-type cell can be drilled *into*.
 *
 * `Exclude`, not `Omit`: `Omit` operates on object types, and applied to a string union it collapses to
 * `{}` — which accepts any value at all, including the `'byCostType'` it was meant to remove. The
 * constraints below would then check nothing and every consumer would need a cast.
 */
export type VarianceDrillingDimension = Exclude<VarianceGroupingDimension, 'byCostType'>

/**
 * A dimension a cost-type cell can be drilled *into*, as a list.
 *
 * Derived rather than written out, so it cannot fall out of step with {@link VARIANCE_GROUPING_DIMENSIONS},
 * and in that list's order — which is the order `/by-cost-type` offers them in when the cell itself names
 * no preference.
 */
export const VARIANCE_DRILLING_DIMENSIONS = VARIANCE_GROUPING_DIMENSIONS.filter(
  (dimension): dimension is VarianceDrillingDimension =>
    dimension !== VARIANCE_GROUPING_DIMENSION_TYPES.byCostType,
)

/* -------------------------------------------------------------------------- */
/* Which filters a breakdown can be scoped by                                  */
/* -------------------------------------------------------------------------- */

/**
 * The kinds of filter a variance request can carry beyond the material scope every breakdown needs.
 *
 * A group is the unit the rule works in, because its params travel together: an `agreementNumber` without
 * its `masterAgreement` names nothing, and a `manualInvestmentId` means something only on a manual-program
 * cell. Each group has its own flat target list below, and a request carrying several is answerable only
 * where those lists agree.
 */
export const VARIANCE_FILTER_GROUPS = [
  'costType',
  'manualInvestment',
  'agreement',
  'customer',
  'budget',
] as const

export type VarianceFilterGroup = (typeof VARIANCE_FILTER_GROUPS)[number]

/**
 * Which breakdowns can answer a cell, for the two sources whose answer does not depend on the cost type.
 *
 * **An imported row names an agreement and a customer**, so both readings apply to it. It has a budget
 * bucket too, but only through its agreement, and re-sourcing the elapsed months to reach it would
 * report different figures than the cell being drilled — so `byBudgetAllocation` is not offered.
 *
 * **A manual program is committed against a budget and against the customers it targets**, and has no
 * agreement at any grain — the one reading it cannot offer.
 *
 * The order within a row is **display order**: it is what the cost-type page offers, top to bottom.
 */
export const DRILL_TARGETS_FOR_COST_TYPE_FILTERS = {
  directImport: ['byCustomer', 'byAgreement'],
  manualProgram: ['byBudgetAllocation', 'byCustomer'],
} as const satisfies Record<
  Exclude<VarianceCostSource, 'leftover'>,
  readonly VarianceDrillingDimension[]
>

/**
 * The same for `leftover`, which is the one source whose answer *does* depend on the cost type.
 *
 * A leftover is a balance, and each kind has exactly one axis. The commercial fund's is the budget
 * bucket's `leftToSpend`, which no customer stands behind. The key-account leftovers are netted per
 * `(customer hierarchy × product category × contract clause)`, which no budget allocation stands
 * behind. Neither belongs to an agreement.
 *
 * `salesDeals` and `goP4p` are **absent on purpose**: they have no leftover line at all, so that cell is
 * structurally zero and there is nothing to break down along any axis. A missing key reads as "nothing
 * can answer this", which is why the lookup below falls back to an empty list rather than throwing.
 *
 * Annotated rather than `as const satisfies`, so indexing it by any cost type yields `… | undefined`
 * and the caller's `?? []` types itself — no cast at the call site.
 */
export const DRILL_TARGETS_FOR_LEFTOVER_FILTERS: Partial<
  Record<VarianceCostType, readonly VarianceDrillingDimension[]>
> = {
  commercialFund: ['byBudgetAllocation'],
  tradePayForPerformance: ['byCustomer'],
  marketingInvestmentInGTO: ['byCustomer'],
}

/**
 * A manual investment sits in exactly one budget bucket and targets many customer hierarchies, so the
 * customer reading is the only one that divides it — by budget it would be a single row restating itself.
 *
 * It applies only where there *is* one, which {@link SOURCES_FOR_MANUAL_INVESTMENT_FILTERS} says.
 */
export const DRILL_TARGETS_FOR_MANUAL_INVESTMENT_FILTERS = [
  'byCustomer',
] as const satisfies readonly VarianceDrillingDimension[]

/**
 * An agreement belongs to one master agreement, which belongs to one budget bucket and one ancestor
 * hierarchy — so neither of those readings divides it either. It does span many accounts, which is the
 * one breakdown left with something to say.
 */
export const DRILL_TARGETS_FOR_AGREEMENT_FILTERS = [
  'byCustomer',
] as const satisfies readonly VarianceDrillingDimension[]

/**
 * A customer holds many master agreements and draws on many budgets, so both readings divide its spend.
 *
 * The filter names a `hierarchy_erp` and never a `sap_erp`: the RAP forecast, the manual investments and
 * the key-account estimates each name a Commercial-Partner code and no customer number, so an account
 * number could not narrow the forecast side at all.
 */
export const DRILL_TARGETS_FOR_CUSTOMER_FILTERS = [
  'byAgreement',
  'byBudgetAllocation',
] as const satisfies readonly VarianceDrillingDimension[]

/**
 * A budget bucket holds many master agreements, so the agreement reading divides it.
 *
 * Not by customer: a bucket reaches a customer only through those agreements, and the leftover balance in
 * it stands behind no customer at all.
 */
export const DRILL_TARGETS_FOR_BUDGET_FILTERS = [
  'byAgreement',
] as const satisfies readonly VarianceDrillingDimension[]

/**
 * Which sources record the axis each filter group names.
 *
 * A source that does not record it cannot narrow by it — not "to zero", but not at all: there is no
 * column to compare. The lists above say which *breakdown* can divide a figure; these say which *source*
 * has the figure to divide in the first place, and the two are independent questions.
 *
 * - An agreement is named only by an imported row. A manual program has none at any grain, and a leftover
 *   is a balance netted per bucket or per `(hierarchy × category × clause)`.
 * - A budget bucket reaches an imported row only *through* its agreements, which is the same restriction
 *   read from the other end.
 * - A customer is named by an imported row and targeted by a manual program. No leftover stands behind
 *   one: the commercial fund's is a bucket's net, with no customer dimension anywhere in its inputs.
 * - A program id exists only where a program was committed.
 */
export const SOURCES_FOR_AGREEMENT_FILTERS = [
  'directImport',
] as const satisfies readonly VarianceCostSource[]

export const SOURCES_FOR_BUDGET_FILTERS = [
  'directImport',
] as const satisfies readonly VarianceCostSource[]

export const SOURCES_FOR_CUSTOMER_FILTERS = [
  'directImport',
  'manualProgram',
] as const satisfies readonly VarianceCostSource[]

export const SOURCES_FOR_MANUAL_INVESTMENT_FILTERS = [
  'manualProgram',
] as const satisfies readonly VarianceCostSource[]

/** Whether a ladder row is one of the five spend partitions, and so has a breakdown to offer at all. */
const isVarianceCostType = (kpiKey: VarianceCostTypeRowKey): kpiKey is VarianceCostType =>
  (VARIANCE_COST_TYPES as readonly string[]).includes(kpiKey)

/**
 * Every breakdown a cost-type cell can be drilled into. Empty when nothing can.
 *
 * A row outside {@link VARIANCE_COST_TYPES} answers with nothing, which covers three cases at once:
 * NTO's `startPrice` and `distributorMargins`, and **every row of the AR ladder**. None of them is spend
 * committed against an agreement, a budget or a customer, so none has an axis to be divided along — the
 * AR ladder reaches that detail by switching the measure to NTO, not by drilling from here.
 *
 * The guard is load-bearing rather than defensive: a route's `kpiKey` enum accepts the union of both
 * ladders (a flat enum being the only shape Hono's RPC input survives), so any of those keys can now
 * actually arrive. Returning `[]` is what turns each of them into the same 422 `goP4p` plus `leftover`
 * already gets, in `assertCompatibleParametersForVarianceDimension`, with no rule added there.
 */
function getCostTypeDrillTargets(
  kpiKey: VarianceCostTypeRowKey,
  source: VarianceCostSource,
): readonly VarianceDrillingDimension[] {
  if (!isVarianceCostType(kpiKey)) return []

  // Narrowing does the rest: the false branch is the two sources the other table is total over.
  return source === 'leftover'
    ? (DRILL_TARGETS_FOR_LEFTOVER_FILTERS[kpiKey] ?? [])
    : DRILL_TARGETS_FOR_COST_TYPE_FILTERS[source]
}

/**
 * Every filter a breakdown can be narrowed by, named as the endpoints name them.
 *
 * All optional: a request carries whichever steps the reader has taken, and the head of the chain carries
 * none. `agreementNumber` is the one param that is not a group of its own — an agreement number is only
 * meaningful under the master agreement it belongs to, so it rides along with `masterAgreement`.
 */
export type VarianceDrillFilters = {
  measure?: VarianceMeasure
  kpiKey?: VarianceCostTypeRowKey
  source?: VarianceCostSource
  manualInvestmentId?: number
  masterAgreement?: string
  agreementNumber?: string
  hierarchyErp?: string
  budgetAllocation?: string
}

/**
 * The target list each group present in these filters imposes, ignoring the cost-type cell.
 *
 * A group that is absent imposes nothing, which is what makes the intersection below the identity for a
 * request carrying no filters at all.
 */
function getNarrowingDrillTargets(
  filters: VarianceDrillFilters,
): readonly (readonly VarianceDrillingDimension[])[] {
  const narrowing: (readonly VarianceDrillingDimension[])[] = []

  /**
   * One group's contribution to the intersection.
   *
   * A group the request does not carry contributes nothing. A group carried on a source that does not
   * record its axis contributes the **empty list** — not the group's targets: there is no column to
   * narrow by, so no breakdown can answer, which is the same reading `goP4p` plus `leftover` already
   * gets. Sending it anyway would report the unfiltered figure under a badge claiming otherwise.
   */
  const narrowBy = (
    isPresent: boolean,
    sources: readonly VarianceCostSource[],
    targets: readonly VarianceDrillingDimension[],
  ) => {
    if (!isPresent) return
    narrowing.push(filters.source && sources.includes(filters.source) ? targets : [])
  }

  narrowBy(
    filters.manualInvestmentId != null,
    SOURCES_FOR_MANUAL_INVESTMENT_FILTERS,
    DRILL_TARGETS_FOR_MANUAL_INVESTMENT_FILTERS,
  )
  narrowBy(
    Boolean(filters.masterAgreement),
    SOURCES_FOR_AGREEMENT_FILTERS,
    DRILL_TARGETS_FOR_AGREEMENT_FILTERS,
  )
  narrowBy(
    Boolean(filters.hierarchyErp),
    SOURCES_FOR_CUSTOMER_FILTERS,
    DRILL_TARGETS_FOR_CUSTOMER_FILTERS,
  )
  narrowBy(
    Boolean(filters.budgetAllocation),
    SOURCES_FOR_BUDGET_FILTERS,
    DRILL_TARGETS_FOR_BUDGET_FILTERS,
  )

  return narrowing
}

/**
 * Every breakdown that can answer a request carrying these filters. Empty when nothing can.
 *
 * **This is the single rule.** Each drill endpoint refuses a request it is not granted here with a 422,
 * and a drill page offers a link only where there is one. Both deriving from this is what stops a button
 * leading to a dead link — they each used to encode the rule separately, and that drift is exactly what
 * went wrong.
 *
 * The cost-type cell leads the intersection because its list is **display order** — what `/by-cost-type`
 * offers, top to bottom. Every other group only removes from it.
 */
export function getValidVarianceDrillingDimensions(
  filters: VarianceDrillFilters,
): readonly VarianceDrillingDimension[] {
  // The AR ladder is not a spend partition — no row of it is money committed against an agreement, a
  // budget or a customer — so nothing can divide one of its cells, whatever else the request carries.
  // A reader reaches that detail by switching the measure to NTO, which is a different cell entirely.
  //
  // It is checked here rather than by the schemas because a cost type *is* a valid key under either
  // measure: `salesDeals` with `measure=ar` names a real figure and a well-formed request, and the
  // reading it asks for simply does not exist — which is a 422 carrying what would have worked, not a
  // 400.
  if (filters.measure === 'ar') return []

  const candidates =
    filters.kpiKey && filters.source
      ? getCostTypeDrillTargets(filters.kpiKey, filters.source)
      : VARIANCE_DRILLING_DIMENSIONS

  const narrowing = getNarrowingDrillTargets(filters)

  return candidates.filter((dimension) =>
    narrowing.every((targets) => (targets as readonly string[]).includes(dimension)),
  )
}

/**
 * Which groups each breakdown can be scoped by — the transpose of the lists above.
 *
 * Derived rather than written out beside them: two tables saying the same thing in opposite directions is
 * precisely the drift this file exists to prevent. `costType` is the union over every cell, because which
 * cells reach a dimension is the cell's question and not this one's.
 */
const DRILL_TARGETS_BY_FILTER_GROUP: Record<
  VarianceFilterGroup,
  readonly VarianceDrillingDimension[]
> = {
  costType: VARIANCE_DRILLING_DIMENSIONS.filter((dimension) =>
    VARIANCE_COST_TYPES.some((kpiKey) =>
      VARIANCE_COST_SOURCES.some((source) =>
        (getCostTypeDrillTargets(kpiKey, source) as readonly string[]).includes(dimension),
      ),
    ),
  ),
  manualInvestment: DRILL_TARGETS_FOR_MANUAL_INVESTMENT_FILTERS,
  agreement: DRILL_TARGETS_FOR_AGREEMENT_FILTERS,
  customer: DRILL_TARGETS_FOR_CUSTOMER_FILTERS,
  budget: DRILL_TARGETS_FOR_BUDGET_FILTERS,
}

/**
 * Which filter groups this breakdown can be scoped by, and therefore which ones a link to it must drop.
 *
 * A breakdown never accepts the group its own table is keyed on, and that falls out of the lists rather
 * than being a second rule beside them: `byAgreement` is absent from the agreement list, `byCustomer` from
 * the customer list, `byBudgetAllocation` from the budget list, and `byCostType` from every one of them.
 */
export function getValidVarianceFilterGroups(
  dimension: VarianceGroupingDimension,
): readonly VarianceFilterGroup[] {
  return VARIANCE_FILTER_GROUPS.filter((group) =>
    (DRILL_TARGETS_BY_FILTER_GROUP[group] as readonly string[]).includes(dimension),
  )
}
